// Para ONDE o botão "Navegar" da ficha do cliente manda o vendedor: pela coordenada ou pelo texto.
//
// Mora aqui, separado da tela, pelo mesmo motivo de `utils/filaOffline.ts`: é a parte em que um erro é
// SILENCIOSO — nada quebra, o vendedor só dirige até o lugar errado — e aqui ela roda em jest. O
// `mobile/jest.config.js` é `testEnvironment: 'node'` sem `jest-expo`, então este arquivo NÃO pode
// importar NADA — nem React Native, Expo ou Firebase, nem tipos (e a cópia do painel, abaixo, depende
// disso para ser byte a byte). Daí as interfaces locais abaixo.
//
// ⚠️ O QUE ESTE MÓDULO CONSERTA. O `openMaps` navegava pela coordenada SEMPRE que ela existia,
// ignorando a qualidade dela. Medido na base (out/2026):
//   • 1.723 clientes com `geoPrecision: 'city'` — a coordenada é o CENTRO DO MUNICÍPIO;
//   • 489 com a coordenada caindo em OUTRO município (`geoCity` ≠ `address.city`), mediana de 474 km.
// O vendedor era mandado ao centro da cidade ou a outra cidade. Agora a coordenada só vale quando é
// confiável; senão vai o endereço em texto, e o Maps geocodifica com rua, bairro e CEP.
// Desde 27/10/2026 quem diz o que é "confiável" é o SERVIDOR, pelo campo `geoNavegavel` do cliente — a
// regra local é só o que vale enquanto o campo não existe (ver "POR QUE O SERVIDOR DECIDE", abaixo).
//
// ⚠️ DUAS CÓPIAS, UMA REGRA. Este arquivo é copiado BYTE A BYTE para `admin/src/utils/navegacaoCliente.ts`:
// o mapa ao vivo do painel (`LiveMapPage`) usa o mesmo `coordenadaConfiavel` para decidir se desenha o
// pino/ETA do destino — o gestor tem de ver o destino pelo MESMO critério que mandou o vendedor navegar.
// O painel é Create React App e não importa de fora de `src/` (mesmo motivo do motor de roteirização),
// por isso a cópia. O teste `admin/src/utils/__tests__/navegacaoEmSincronia.test.ts` falha se as duas
// divergirem. Mude AQUI (o app é a fonte de verdade, coberto por jest) e copie por cima do painel:
//   cp mobile/src/utils/navegacaoCliente.ts admin/src/utils/navegacaoCliente.ts

/** Forma mínima do endereço do cliente. Interface LOCAL de propósito — ver o cabeçalho. */
export interface EnderecoNav {
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
}

/** Forma mínima do cliente para decidir a navegação. */
export interface ClienteNav {
  location?: { latitude?: unknown; longitude?: unknown } | null;
  geoPrecision?: string | null;
  geoCity?: string | null;
  /**
   * Decisão do SERVIDOR: false = pelo texto (vale sempre, salvo 'gps'); true = pela coordenada, desde
   * que ela caia na cidade do cadastro; ausente = regra local. Ver `coordenadaConfiavel`.
   */
  geoNavegavel?: boolean | null;
  address?: EnderecoNav | null;
}

// ============================================================ cidade

// Porte FIEL de `_normCidade`/`_cidadeBate` de functions/index.js — é a mesma régua que a rotina de
// geocodificação usa para gravar e revalidar o `geoCity`. Divergir daqui faria o app discordar do
// servidor sobre qual coordenada está na cidade certa. Se mudar lá, mude aqui.
export function normCidade(s: unknown): string {
  return String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Casamento EXATO ("São José" não casa "São José dos Campos"); LENIENTE quando falta um dos lados. */
export function cidadeBate(a: unknown, b: unknown): boolean {
  const x = normCidade(a);
  const y = normCidade(b);
  return !x || !y || x === y;
}

// ============================================================ coordenada

// Precisões que valem como destino QUANDO a coordenada cai na cidade do cadastro ('gps' decide antes,
// sem olhar cidade). 'street'/'postal' saíram em 27/10/2026 e 'city' nunca esteve — ver a nota abaixo.
// 'manual' e `undefined`/null — as duas são coordenada DIGITADA — têm regra própria, mais dura: a cidade
// CONFIRMADA (ver `cidadeConfirmada` e "POR QUE 'manual' NÃO É LENIENTE", abaixo).
const PRECISOES_NA_CIDADE = new Set(['erp', 'cnefe']);
// Sem rede (ver `coordenadaParaNavegarOffline`): as de cima mais 'street'/'postal', que só perdem para o
// texto quando há texto — e offline não há. 'manual' fica de fora também aqui.
const PRECISOES_OFFLINE = new Set(['erp', 'cnefe', 'street', 'postal']);

/**
 * Cidade do cadastro sem os marcadores que o ERP gruda no nome: "INATIVAVILHENA", "INATIVA - SANTO
 * ANTONIO DE JESUS", "INATIVASERRA - NÃO USAR", "INATIVAVAZIO" (36 clientes na base de out/2026).
 * Sem isto a cidade nunca bate com o `geoCity` e o texto mandado ao Maps leva um município inexistente.
 * Grafias que divergem por pouco (ISIDORO/IZIDORO, ASSU/AÇU) continuam divergindo DE PROPÓSITO: o
 * casamento exato é o que impede "São José" de casar com "São José dos Campos".
 */
export function cidadeDoCadastro(city: string | null | undefined): string {
  let s = String(city || '').normalize('NFC').replace(/\s+/g, ' ').trim();
  if (!s || ehPlaceholder(s)) return '';
  s = s.replace(/^INATIVA[\s-]*/i, '');
  s = s.replace(/^N[AÃ]O USAR[\s-]*/i, '').replace(/[\s-]*N[AÃ]O USAR$/i, '').trim();
  return /^VAZIO$/i.test(s) ? '' : s;
}

// O DF é UM município (Brasília), mas o reverse do servidor grava em `geoCity` a Região Administrativa
// (Vicente Pires, Taguatinga, Plano Piloto, Guará...). Medido na base: NENHUM cliente 'erp' do DF tem
// `geoCity` "Brasília" — pela régua do nome, toda coordenada do DF já revalidada daria "outro
// município". No DF a pergunta certa é geográfica: a coordenada cai dentro do DF? (Retângulo do DF;
// separou os 59 divergentes da base em 54 dentro e os 5 que de fato caem em outro estado.)
// ⚠️ O `_cidadeBate` do servidor tem o MESMO furo, e o `cidadeBate` acima continua fiel a ele — a
// exceção mora só aqui, na decisão de navegação.
function ehBrasilia(c: ClienteNav | null | undefined): boolean {
  if (String(c?.address?.state || '').trim().toUpperCase() !== 'DF') return false;
  const cidade = normCidade(cidadeDoCadastro(c?.address?.city));
  return cidade === '' || cidade === 'BRASILIA';
}
function dentroDoDF(c: ClienteNav | null | undefined): boolean {
  const lat = c?.location?.latitude as number;
  const lng = c?.location?.longitude as number;
  return lat >= -16.06 && lat <= -15.49 && lng >= -48.29 && lng <= -47.30;
}
/** A coordenada cai na cidade do cadastro (leniente com um lado vazio, como o servidor; DF à parte). */
function naCidadeDoCadastro(c: ClienteNav | null | undefined): boolean {
  if (ehBrasilia(c)) return dentroDoDF(c);
  return cidadeBate(c?.geoCity, cidadeDoCadastro(c?.address?.city));
}
/**
 * A cidade da coordenada foi CONFIRMADA: `geoCity` e a cidade do cadastro PRESENTES e batendo (no DF,
 * coordenada dentro do DF). Sem a leniência do `naCidadeDoCadastro` — `geoCity` null/ausente aqui é
 * "ninguém conferiu ainda", não "pode ser".
 */
function cidadeConfirmada(c: ClienteNav | null | undefined): boolean {
  return !!normCidade(c?.geoCity) && !!normCidade(cidadeDoCadastro(c?.address?.city)) && naCidadeDoCadastro(c);
}

/** A coordenada existe e é um número utilizável (0,0 é o "vazio" de importação, não um lugar). */
export function temCoordenada(c: ClienteNav | null | undefined): boolean {
  const lat = c?.location?.latitude;
  const lng = c?.location?.longitude;
  if (typeof lat !== 'number' || typeof lng !== 'number') return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  return !(lat === 0 && lng === 0);
}

/** Coordenada 'gps' = confirmada NA PORTA, no check-in. Não depende de `geoCity` nem de endereço. */
export function coordenadaGps(c: ClienteNav | null | undefined): boolean {
  return temCoordenada(c) && c?.geoPrecision === 'gps';
}

// ⚠️ POR QUE 'street'/'postal' PASSARAM A IR PELO TEXTO (27/10/2026). Medido com verdade de campo — a
// posição dos check-ins — contra a coordenada gravada:
//   • 'street'/'postal': muitas vieram do Google. Navegar pelo TEXTO do endereço (o Maps resolve ao
//     vivo) acerta mais a porta — 'street' 70% contra 52% até 100 m; 'postal' 77% contra 38% — e acima
//     de 1 km os dois empatam: guardar o ponto não compra nada. E os termos do Google PROÍBEM guardar
//     lat/lng por mais de 30 dias e exibi-la em mapa que não seja Google.
//   • 'erp' (n=342, clientes que eram 'erp' na visita): mediana 92 m, 63,7% até 300 m, 19,9% acima de
//     1 km. Navegar pelo texto (proxy: 1º resultado do Google) dá o MESMO — 62,3% / 19,9%. Trocar a
//     fonte não resolve; por isso 'erp' na cidade continua indo pela coordenada (que funciona offline).
//   • O que separa o 'erp' bom do ruim é o CONSENSO com o CNEFE (IBGE, Censo 2022). ERP e CNEFE a até
//     150 m um do outro (98 de 342): 92,9% até 300 m, 4,1% acima de 1 km. Divergindo (>150 m, n=51): o
//     ERP cai para 37,3% / 29,4% e o CNEFE fica em 68,6% / 9,8% (mais perto em 32 de 51). Sem CNEFE
//     casado (n=193), o ERP perto (≤500 m) do melhor nível aproximado do CNEFE dá 71,7% / 9,1%; de
//     500 m a 2 km, 48,8% / 27,9%; acima de 2 km, 27,9% / 58,1% — e aí nenhuma fonte acerta (texto
//     46,5%, CNEFE aproximado 65,1%): é o ENDEREÇO que está errado, tarefa do comercial no Sankhya.
//
// ⚠️ POR QUE O SERVIDOR DECIDE (`geoNavegavel`). Esse cruzamento ERP × CNEFE × check-in mora no
// servidor, e o APK que vai a campo (1.7.x, `sdk57-reconciliado`) NÃO tem expo-updates: não recebe OTA.
// Uma regra só no app ficaria congelada até o próximo APK. Então o app OBEDECE ao campo `geoNavegavel`
// do cliente (true = coordenada, false = texto) e só cai na regra local quando ele não é booleano (hoje
// ninguém o grava ainda). Ajustar depois = o servidor regravar o campo (e republicar o índice Tier B,
// que o carrega para a semente do acesso total) — sem APK novo. Duas coisas ficam ACIMA dele: 'gps'
// (verdade de campo) e "sem coordenada" (não há o que navegar).
// ⚠️ E o `true` NÃO é cheque em branco: vale só com a coordenada na cidade do cadastro e sem `geoCity: ''`
// (revisão de 27/10/2026). O campo não está amarrado à coordenada que julgou — um `true` que sobrou de
// antes de a coordenada ou o endereço mudarem, ou vindo de um classificador que não olha `geoCity`,
// mandaria o vendedor a OUTRO município, e este APK não teria como desconfiar dele nunca mais. Medido no
// snapshot local: em 426 clientes 'erp' com coordenada a régua de cidade é a ÚNICA proteção (7 com
// `geoCity: ''` e 419 em outro município ou com o DF fora do retângulo; mediana de 474 km). O servidor
// segue podendo VETAR qualquer coisa e PROMOVER qualquer precisão ('street', 'city', 'aprox'...) na
// cidade certa; para liberar um rótulo de fronteira, ele corrige o `geoCity` junto com a decisão.
//
// ⚠️ POR QUE 'manual' NÃO É LENIENTE (revisão adversarial de 27/10/2026). 'manual' é a coordenada que
// alguém acabou de DIGITAR no painel (`ClientFormModal`), que grava junto `geoCity: null` (revalidar) e
// apaga o `geoNavegavel`. Com a leniência do 'erp' (`geoCity` null → confiável), uma latitude/longitude
// trocada ou com o sinal esquecido mandaria o vendedor para o mar — ou para outro estado — até a rotina
// do servidor rodar. Então 'manual' segue a régua da precisão AUSENTE, que também é coordenada digitada:
// sem `geoNavegavel`, só com o `geoCity` presente e batendo com a cidade do cadastro (`cidadeConfirmada`),
// online e offline. Enquanto isso o vendedor vai pelo texto. No snapshot de 27/10/2026 nenhum cliente é
// 'manual' ainda (o painel passa a gravar a precisão nesta rodada): a mudança não tira ninguém da
// coordenada hoje.
//
// Precisões: 'gps' = confirmada na porta, no check-in; 'erp' = coordenada do Sankhya; 'cnefe' = rua +
// número casados no CNEFE do IBGE; 'manual' = coordenada digitada no painel (`ClientFormModal`);
// 'street'/'postal' = geocodificador; 'aprox' = CNEFE só aproximado (CEP, via ou bairro); 'city' =
// centro do município.

/**
 * A coordenada pode ser usada como destino? Nesta ordem — a primeira regra que responde decide:
 * 1. Sem coordenada utilizável (`temCoordenada`): nunca — nem 'gps', nem o servidor mandando.
 * 2. 'gps': SEMPRE, mesmo com `geoCity` divergente ou `geoNavegavel: false` — foi medida no cliente, e
 *    o endereço cadastrado é que pode estar errado.
 * 3. `geoNavegavel === false`: nunca — o veto do servidor vale sempre.
 * 4. `geoCity === ''` (e não null): o reverse do servidor DESISTIU depois de 3 tentativas sem achar
 *    município nenhum (functions/index.js, ramo "desiste: converge") — o próprio servidor chama essa
 *    coordenada de suspeita ("dígito trocado caindo no mar"). Medido: os 7 casos da base, todos 'erp',
 *    alguns no Atlântico. Nunca — nem com `geoNavegavel: true`. (Na regra 'cnefe2022-v2' o servidor
 *    passa a gravar '' também para "ponto fora do município do cadastro ou sem como confirmar".)
 * 5. `geoNavegavel === true`: qualquer precisão, mas só na cidade do cadastro (mesma régua do item 6;
 *    ver "POR QUE O SERVIDOR DECIDE" acima).
 * 6. 'erp' | 'cnefe': só se a cidade onde a coordenada cai (`geoCity`) bater com `address.city` — ou
 *    se um dos lados faltar (mesma leniência do servidor; `geoCity` null/ausente é cliente ainda não
 *    revalidado). No DF, só se cair dentro do DF (ver `ehBrasilia`).
 * 7. 'manual' e precisão AUSENTE (legado: coordenada digitada no painel antes de ele gravar 'manual'):
 *    só com a cidade CONFIRMADA — `geoCity` e `address.city` presentes e batendo (`cidadeConfirmada`).
 *    Sem confirmação, não: é coordenada digitada à mão que ninguém conferiu (ver "POR QUE 'manual' NÃO
 *    É LENIENTE", acima).
 * 8. Qualquer outra — 'street', 'postal' (ver a nota acima), 'city', 'aprox' ou desconhecida: nunca.
 */
export function coordenadaConfiavel(c: ClienteNav | null | undefined): boolean {
  if (!temCoordenada(c)) return false;
  const p = c?.geoPrecision;
  if (p === 'gps') return true;
  if (c?.geoNavegavel === false) return false;
  if (c?.geoCity === '') return false;
  if (c?.geoNavegavel === true) return naCidadeDoCadastro(c);
  if (p && PRECISOES_NA_CIDADE.has(p)) return naCidadeDoCadastro(c);
  if (p == null || p === '' || p === 'manual') return cidadeConfirmada(c);
  return false;
}

/**
 * SEM REDE não há navegação por texto: o Maps precisa geocodificar o endereço ao vivo. Quando a ficha
 * não conseguiu ler o cadastro (offline ou teto estourado — ver `openMaps`), esta é a régua para usar a
 * coordenada que o app já tem em mãos (a semente):
 * - o que `coordenadaConfiavel` aceita, aceita (inclusive 'gps' e `geoNavegavel: true` na cidade);
 * - `geoNavegavel === false`: não — o servidor disse que essa coordenada leva ao lugar errado;
 * - 'erp' | 'cnefe' | 'street' | 'postal' na cidade do cadastro: sim. 'street'/'postal' perdem para o
 *   texto ONLINE, mas empatam com ele acima de 1 km — e offline o texto não existe;
 * - 'manual' só com a cidade CONFIRMADA, como online (e aí `coordenadaConfiavel` já aceitou): sem rede,
 *   ir ao mar por uma lat/lng trocada continua pior que não navegar;
 * - `geoCity: ''` (reverse desistiu), coordenada em OUTRO município (mediana de 474 km), 'city',
 *   'aprox', 'manual' ou sem precisão sem a cidade confirmada, ou sem coordenada: não. Melhor não
 *   navegar que ir ao lugar errado.
 */
export function coordenadaParaNavegarOffline(c: ClienteNav | null | undefined): boolean {
  if (coordenadaConfiavel(c)) return true;
  if (!temCoordenada(c) || c?.geoNavegavel === false || c?.geoCity === '') return false;
  const p = c?.geoPrecision;
  return !!p && PRECISOES_OFFLINE.has(p) && naCidadeDoCadastro(c);
}

// ============================================================ endereço em texto

// Tipos de logradouro → forma canônica. Só o que aparece na base e não colide com nome próprio comum
// (VILA, por exemplo, fica DE FORA: "RUA VILA RICA" é nome de rua).
const TIPOS: Record<string, string> = {
  R: 'RUA', RUA: 'RUA',
  AV: 'AV', AVE: 'AV', AVN: 'AV', AVENIDA: 'AV',
  ROD: 'ROD', RDV: 'ROD', RODOVIA: 'ROD',
  EST: 'EST', ESTR: 'EST', ESTRADA: 'EST',
  TV: 'TV', TVS: 'TV', TRV: 'TV', TRAV: 'TV', TRAVESSA: 'TV',
  PC: 'PCA', PCA: 'PCA', PRACA: 'PCA',
  AL: 'AL', ALAMEDA: 'AL',
  LRG: 'LRG', LGO: 'LRG', LARGO: 'LRG',
  Q: 'QD', QD: 'QD', QUADRA: 'QD',
  PSG: 'PSG', PASSAGEM: 'PSG',
  BC: 'BC', BECO: 'BC',
  LD: 'LD', LADEIRA: 'LD',
  LOG: 'LOG', LOGRADOURO: 'LOG',
  OUT: 'OUT',
};

// Siglas de UF: "ROD AL 101" é a rodovia estadual AL-101, não "Rodovia Alameda 101".
const UFS = new Set([
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE',
  'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
]);

const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');
/** Token sem acento, maiúsculo e sem pontuação nas pontas ("Av." → "AV", "PRAÇA" → "PRACA"). */
const chave = (t: string) => semAcento(t).toUpperCase().replace(/^[^A-Z0-9]+|[^A-Z0-9]+$/g, '');
const tipoDe = (t: string | undefined): string | null => (t ? TIPOS[chave(t)] ?? null : null);
/** "12", "8", "C13", "101", "040", "R-8": pedaço de código, não palavra. */
const ehCodigo = (t: string | undefined) => !!t && /\d/.test(t) && /^[A-Z0-9-]{1,6}$/.test(chave(t));

/**
 * Placeholder do ERP ("<SEM ENDERECO>", "< SEM  BAIRRO >", "<SEM DESCRIÇÃO>", "<DEFINIR> 16407"): não
 * é endereço. Todo valor que começa com "<" é marcador do Sankhya, não nome de lugar.
 */
function ehPlaceholder(s: string): boolean {
  if (/^\s*</.test(s)) return true;
  const n = normCidade(s);
  return n === 'SEM' || n.startsWith('SEM ');
}

// Bairro DEFAULT do Sankhya: aparece em 77 clientes de 71 municípios diferentes, nenhum deles Barueri
// ou Santana de Parnaíba. Mandar isto para o Maps puxa a busca para o Alphaville de SP. Por PREFIXO: a
// forma por extenso ("...CENTRO INDUSTRIAL E EMPRESARIAL/ALPHAV") também aparece.
const ehBairroPadraoSankhya = (bairro: string) => normCidade(bairro).startsWith('ALPHAVILLE CENTRO INDUST');

/** Pontuação solta nas pontas ("CENTRO," → "CENTRO"); o "." final fica (abreviação: "J. K."). */
const semPontaSolta = (s: string) => s.replace(/^[\s,;.-]+|[\s,;-]+$/g, '');

/**
 * Limpa o logradouro que vem do ERP. O Sankhya concatena TIPO + NOMEEND, e o NOMEEND muitas vezes já
 * traz o tipo: "R RODOVIA PE 15 ,", "AV AV CELSO...", "R PRACA...", "ROD ROD BR-262", "RUA RUA X",
 * "R (Rua) X". Regra: se a 1ª palavra é tipo e a 2ª também, a 1ª sai — EXCETO quando a 2ª é uma letra
 * solta ou sigla de UF seguida de código, que é nome de verdade ("RUA R 12", "ROD AL 101").
 * Nomes sem tipo duplicado ("AV CONTORNO") passam intactos.
 */
export function limparLogradouro(street: string | null | undefined): string {
  let s = String(street || '').replace(/\s+/g, ' ').trim();
  if (!s || ehPlaceholder(s)) return '';
  // "S/N" dentro do logradouro ("Av CASTELO BRANCO S/N", "RUA JC1, S/N, QD. 24A"): o número mora no
  // campo número; aqui é só ruído. Só a forma COM barra — "SN" solto pode ser nome ("Tv SN 21").
  s = s.replace(/(^|[\s,;])S\s*\/\s*N[º°o]?\.?(?=$|[\s,;])/gi, '$1').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  const t = s.split(' ');
  if (t.length >= 3 && tipoDe(t[0])) {
    const paren = /^\((.+)\)$/.exec(t[1]);
    if (paren && tipoDe(paren[1])) {
      // "R (Rua) SARGENTO SIMPLICIO" → "Rua SARGENTO SIMPLICIO"
      t.splice(0, 2, paren[1]);
    } else if (tipoDe(t[1])) {
      const k2 = chave(t[1]);
      const nomeDeVerdade = ehCodigo(t[2]) && (k2.length === 1 || UFS.has(k2));
      if (!nomeDeVerdade) t.shift();
    }
  }
  s = t.join(' ');
  return s
    .replace(/\s+([,;])/g, '$1')       // "PE 15 ," → "PE 15,"
    .replace(/([,;])(?:\s*[,;])+/g, '$1') // vírgulas repetidas
    .replace(/^[\s,;.-]+|[\s,;-]+$/g, ''); // pontuação solta nas pontas (o "." final de "ROD." fica)
}

/**
 * Número do imóvel, ou '' para "S/N", "SN", "S/Nº", "S/N2", "0", "00", vazio e afins sem dígito — e
 * para o que começa com palavra de 3+ letras ("LOTE7", "CASA4", "BOX02", "QDR9"), que não é número de
 * porta e só põe ruído na busca. Vírgula/ponto soltos nas pontas saem ("200," virava ",,").
 */
export function numeroUtil(n: string | null | undefined): string {
  const s = String(n || '').trim().replace(/^[\s,;.-]+|[\s,;.-]+$/g, '');
  const k = semAcento(s).toUpperCase();
  if (/^S\s*[/.]?\s*N(?![A-Z])/.test(k)) return ''; // S/N, SN, S/Nº, S.N., S/N2
  if (/^[A-Z]{3,}/.test(k)) return '';                // LOTE7, CASA 4, BOX02, QDR9
  const d = k.replace(/[^A-Z0-9]/g, '');
  if (!/\d/.test(d)) return '';          // "CASA", vazio
  if (/^0+$/.test(d)) return '';         // 0, 00, 000
  return s;
}

/** CEP como 00000-000 quando há 8 dígitos (com ou sem hífen); senão ''. */
export function cepFormatado(z: string | null | undefined): string {
  const d = String(z || '').replace(/\D/g, '');
  if (d.length !== 8 || /^0+$/.test(d)) return '';
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

// "BR-232", "ES 164", "MA014", "RST-509": BR, sigla de UF ou prefixo estadual gaúcho (RST/ERS/VRS) +
// 2-3 dígitos. Sigla de UF, e não duas letras quaisquer, para "QD 25" não virar rodovia.
const CODIGO_RODOVIA = new RegExp(`\\b(BR|RST|ERS|VRS|${Array.from(UFS).join('|')})[- ]?\\d{2,3}\\b`);

// Rodovia/estrada: tipo canônico ROD/EST, ou um código de rodovia no nome ("OUT BR-232", "Rod ES-164").
function ehRodovia(logradouro: string): boolean {
  const tipo = tipoDe(logradouro.split(' ')[0]);
  if (tipo === 'ROD' || tipo === 'EST') return true;
  const n = semAcento(logradouro).toUpperCase();
  return /\bRODOVIA\b|\bESTRADA\b/.test(n) || CODIGO_RODOVIA.test(n);
}

/** "KM 16" a partir do complemento ("KM 16", "km 6", "KM: 40;", "KM- 160", "KM03", "KM 21,5"). */
function kmDoComplemento(complemento: string | null | undefined): string {
  const m = /\bKM\s*[:.-]?\s*(\d+(?:[.,]\d+)?)/i.exec(String(complemento || ''));
  return m ? `KM ${m[1]}` : '';
}

/** "BR 163" de um complemento que traz a rodovia E o KM ("ROD BR 163 KM 160"); senão ''. */
function rodoviaDoComplemento(complemento: string | null | undefined): string {
  const n = semAcento(String(complemento || '')).toUpperCase();
  const m = CODIGO_RODOVIA.exec(n);
  return m && kmDoComplemento(n) ? m[0] : '';
}

/**
 * Texto para o Maps geocodificar: "logradouro, [KM n,] número, bairro, cidade - UF, CEP", só com as
 * partes que existem. O complemento NÃO entra (SALA, LOJA, APTO pioram a busca) — a única exceção é o
 * KM de rodovia, que nessa base mora no complemento e é o que localiza o cliente na estrada.
 * Sem logradouro, número também não entra (um número solto na cidade não aponta para nada) — mas se o
 * COMPLEMENTO traz a rodovia e o KM ("ROD BR 163 KM 160"), ela vira o logradouro: é a única coisa que
 * localiza o cliente. (Com logradouro de rua comum, a rodovia do complemento continua de fora: a rua e
 * o número é que levam à porta.)
 * Sem cidade nem UF (cadastro todo de placeholders), acrescenta ", Brasil": um CEP solto é ambíguo.
 */
export function enderecoParaNavegar(a: EnderecoNav | null | undefined): string {
  if (!a) return '';
  const logradouroCadastro = limparLogradouro(a.street);
  const logradouro = logradouroCadastro || rodoviaDoComplemento(a.complement);
  const km = logradouro && ehRodovia(logradouro) && !/\bKM/i.test(logradouro) ? kmDoComplemento(a.complement) : '';
  const numero = logradouroCadastro ? numeroUtil(a.number) : '';
  const bairroBruto = semPontaSolta(String(a.neighborhood || '').replace(/\s+/g, ' ').trim());
  const bairro = bairroBruto && !ehPlaceholder(bairroBruto) && !ehBairroPadraoSankhya(bairroBruto) ? bairroBruto : '';
  const cidade = cidadeDoCadastro(a.city);
  const ufBruta = String(a.state || '').trim().toUpperCase();
  const uf = /^[A-Z]{2}$/.test(ufBruta) ? ufBruta : '';
  const cidadeUf = [cidade, uf].filter(Boolean).join(' - ');
  const partes = [logradouro, km, numero, bairro, cidadeUf, cepFormatado(a.zipCode)].filter(Boolean);
  if (partes.length && !cidadeUf) partes.push('Brasil');
  return partes.join(', ');
}
